import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { change, Field, touch } from 'redux-form';
import moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import { pathOr, isEmpty, isNil, range } from 'ramda';

import {
  extractCurrentDateFormat,
  renderCheckboxField,
  renderDateField,
  renderSelectField,
} from '../../../../../utils/redux-form-helper';
import {
  calculateLeaseEndDate,
  calculateLeaseEndDateV2,
  generateNumberOfKeys,
  getLeaseTermOptions,
} from '../../../../../utils/lease-helpers.js';
import ElementWithPermissions from '../../../../../components/ElementWithPermissions';

import EditLeaseDataLink from '../../EditLeaseDataLink';

import messages from './messages';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useExpirationLimitsPropertyToggle from '../../../../../hooks/data-fetching/useExpirationLimitsPropertyToggle';
import usePendingMoveInValidation from './usePendingMoveInValidation';
import { useStartTransferEligibility } from '../../../../ResidentProfile/StartTransferModal/hooks';
import { getRentableItemsProratedAmount, getBaseOtherAmount } from '../utils';

export const LeaseBasicsRightColumn = ({
  actions: { change, touch },
  formValues,
  intl,
  isEditable,
  lease,
  leaseEndDate,
  leaseTerms,
  householdId,
  residentId,
  showEditLink,
  showLeaseDataModal,
  unit,
  valid,
  transferInformation,
  updateLeaseDate,
  formAsyncErrors,
  selectedProperty,
  promptToaster,
  noticeToVacate,
  setProratedRentAmount,
  setProratedOtherAmount,
  resetProratedRentAutoFilledChangeStatus,
  monthlyRecurringCharges,
  onPendingCheckUpdate,
  householdRentableItems,
  petCount = 0,
  isShortTermLease = false,
  notifyTrackedInputChanged,
}: any) => {
  const {
    leaseEndEnhance,
    leaseExpirationMgmtV1,
    automatedLeaseChargesP1,
    naur,
    unitTransfers: unitTransfersFlag,
  } = useFlags();

  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const { data: eligibilityData } = useStartTransferEligibility(
    unitTransfersFlag && lease?.isTransfer ? organizationId : '',
    unitTransfersFlag && lease?.isTransfer ? propertyId : '',
    unitTransfersFlag && lease?.isTransfer ? householdId : '',
  );
  // Lock the Scheduled Move-In Date when this lease was created through the automated
  // unit-transfer flow (ENG-30131). The eligibility endpoint exposes the transferLeaseId
  // so we can match it against the current lease without a separate fetch.
  const moveInDateLockedByTransfer =
    unitTransfersFlag &&
    lease?.isTransfer &&
    eligibilityData?.transferLeaseId === lease?.id;

  const leaseEndDateDisabled =
    !formValues.startDate ||
    !formValues.leaseTermId ||
    formValues.leaseTermId === 'default' ||
    !isEditable;

  const isAffordableUnit = pathOr(false, ['floorPlan', 'isAffordable'], unit);

  const { pendingMoveInError, isCheckingPendingMoveIn } =
    usePendingMoveInValidation({
      unitId: unit?.id,
      moveInDate: formValues.moveInDate,
      isAffordableUnit,
      naurFlagEnabled: !!naur,
      intl,
    });

  useEffect(() => {
    if (onPendingCheckUpdate) {
      onPendingCheckUpdate({
        isChecking: isCheckingPendingMoveIn,
        hasError: !!pendingMoveInError,
      });
    }
  }, [isCheckingPendingMoveIn, pendingMoveInError, onPendingCheckUpdate]);

  const isValidDate = (calendarDate: any) => {
    if (!isNil(transferInformation) && !isEmpty(transferInformation)) {
      return moment(transferInformation.previousResidentMoveOutDate).isBefore(
        calendarDate,
      );
    } else {
      const unitLeases = pathOr([], ['leases'], unit);
      const unitStatus = pathOr('', ['unitStatus', 'description'], unit);
      if (!unitStatus.includes('Occupied')) {
        return true;
      }
      const lastLease = unitLeases.filter((l) => l.id !== lease.id)[0];
      const moveOutDate = pathOr(
        '',
        ['noticeToVacate', 'moveOutDate'],
        lastLease,
      );
      return !moveOutDate ? true : moment(moveOutDate).isBefore(calendarDate);
    }
  };

  const onChangeLeaseStartDate = (leaseTermId: any) => (startDate: any) => {
    updateLeaseDate('startDate', startDate);
    // ENG-30333 / FINDING 2 — the user-intent signal for useRecommendedLeaseRent's
    // recalculation arm. Fired synchronously, on every keystroke, exactly like the rest of
    // this handler: the settling that keeps a half-typed date from producing a pricing
    // request per character happens inside the hook (on the derived pricing date it
    // debounces and validates), not here. Debouncing THIS call previously deferred the
    // prorated-rent/other recompute below by 300ms and dropped it on unmount — a regression
    // to behaviour that predates REBA and has nothing to do with it.
    notifyTrackedInputChanged?.();
    if (automatedLeaseChargesP1) {
      const baseOtherAmount = getBaseOtherAmount({
        monthlyRecurringCharges,
        monthlyPetRent: formValues?.monthlyPetRent,
        shortTermFee: formValues?.shortTermFee,
        petCount,
        isShortTermLease,
      });
      const rentableItemsProratedAmount = getRentableItemsProratedAmount(
        householdRentableItems,
        startDate,
      );

      setProratedRentAmount({
        startDate: moment(startDate),
        rentAmount: formValues.leasedRent,
        updateFn: (val) => {
          change('FormLeaseDataTab', 'proratedRent', val);
          resetProratedRentAutoFilledChangeStatus();
        },
      });
      setProratedOtherAmount({
        startDate: moment(startDate),
        otherAmount: baseOtherAmount,
        updateFn: (val) => {
          const baseProratedOtherAmount = parseFloat(val);
          const safeBaseAmount = isNaN(baseProratedOtherAmount)
            ? 0
            : baseProratedOtherAmount;
          const finalProratedOtherAmount = (
            safeBaseAmount + rentableItemsProratedAmount
          ).toFixed(2);
          change('FormLeaseDataTab', 'proratedOther', finalProratedOtherAmount);
          resetProratedRentAutoFilledChangeStatus();
        },
      });
    }
    if (
      moment.isMoment(startDate) &&
      formValues.leaseTermId &&
      formValues.leaseTermId !== 'default'
    ) {
      const endDate = leaseEndEnhance
        ? calculateLeaseEndDateV2(
            startDate,
            leaseTermId,
            leaseTerms,
            selectedProperty,
          )
        : calculateLeaseEndDate(
            startDate,
            leaseTermId,
            leaseTerms,
            selectedProperty?.isSetLeaseEndDateToEndOfMonth,
          );
      touch('FormLeaseDataTab', 'endDate');
      change('FormLeaseDataTab', 'endDate', endDate);
    } else {
      change('FormLeaseDataTab', 'endDate', null);
    }
  };

  const onChangeLeaseTermId =
    (startDate: any) =>
    ({ target: { value } }: any) => {
      // ENG-30333 — see onChangeLeaseStartDate above: same trigger, same reasoning. This also
      // covers Scenario 2's empty -> set case (choosing a term for the first time), since a
      // real <Field> onChange fires identically whether the previous value was 'default' or a
      // real term id — there is no "was this already populated" check here or in the hook.
      notifyTrackedInputChanged?.();
      const momentStartDate = startDate ? moment(startDate) : moment('----');
      if (momentStartDate.isValid() && leaseTerms) {
        const endDate = leaseEndEnhance
          ? calculateLeaseEndDateV2(
              momentStartDate,
              value,
              leaseTerms,
              selectedProperty,
            )
          : calculateLeaseEndDate(
              momentStartDate,
              value,
              leaseTerms,
              selectedProperty?.isSetLeaseEndDateToEndOfMonth,
            );
        touch('FormLeaseDataTab', 'endDate');
        change('FormLeaseDataTab', 'endDate', endDate);
      } else {
        change('FormLeaseDataTab', 'endDate', null);
      }
    };

  const handleMoveInDateChange = (moveInDate: any) => {
    updateLeaseDate('moveInDate', moveInDate);
    // ENG-30333 / FINDING 2 — see onChangeLeaseStartDate above: same trigger, same reasoning.
    // moveInDate is itself a separate tracked input from startDate (Finding 4) — a renewal's
    // moveInDate is the household's original move-in date, not the date this lease's rent
    // takes effect, but an edit to it must still be eligible to recalculate (the hook decides
    // what to actually price off).
    notifyTrackedInputChanged?.();
  };

  const [endDateChanged, setEndDateChanged] = useState(false);
  const [isEndDateOverLimit, setIsEndDateOverLimit] = useState(
    Boolean(lease.overrideLeaseExpirationLimitUpdatedAt),
  );

  useEffect(() => {
    setIsEndDateOverLimit(Boolean(lease.overrideLeaseExpirationLimitUpdatedAt));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(lease.overrideLeaseExpirationLimitUpdatedAt)]);

  const [overLimitMap, setOverLimitMap] = useState({});

  useEffect(() => {
    if (formAsyncErrors?.endDate) {
      setOverLimitMap((p) => ({
        ...p,
        [moment(formValues.endDate).format('YYYY-MM')]: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formAsyncErrors?.endDate]);

  useEffect(() => {
    if (formValues.overrideLeaseExpirationLimit === false) {
      setIsEndDateOverLimit(
        overLimitMap[moment(formValues.endDate).format('YYYY-MM')] ?? false,
      );
    }
  }, [
    overLimitMap,
    formValues.endDate,
    formValues.overrideLeaseExpirationLimit,
  ]);

  const onChangeEndDate = (date) => {
    change('FormLeaseDataTab', 'endDate', date);
    change('FormLeaseDataTab', 'overrideLeaseExpirationLimit', false);
    setEndDateChanged(true);
  };

  useEffect(() => {
    setEndDateChanged(false);
  }, [lease.overrideLeaseExpirationLimitUpdatedAt]);

  const { isLeaseExpirationLimitsActive } = useExpirationLimitsPropertyToggle(
    selectedProperty?.organizationId,
    selectedProperty?.id,
    promptToaster,
  );

  return (
    <Col xs={12} sm={6}>
      <Row>
        <Col xs={6}>
          <label className="required">
            {intl.formatMessage(messages.numberOfKeysLabel)}
          </label>
        </Col>
        <Col xs={5}>
          <div className="form-group">
            <Field
              name="numberOfKeys"
              component={renderSelectField}
              options={generateNumberOfKeys(11)}
              disabled={!isEditable}
            >
              <option value="default">Choose</option>
            </Field>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <label className="required">
            {intl.formatMessage(messages.scheduledMoveInLabel)}
          </label>
        </Col>
        <Col xs={5}>
          <Field
            bsSize="md"
            className="input-group"
            component={renderDateField}
            dateFormat={extractCurrentDateFormat(intl)}
            disabled={
              !isEditable || lease.isRenewal || moveInDateLockedByTransfer
            }
            isValidDate={isValidDate}
            name="moveInDate"
            placeholder={extractCurrentDateFormat(intl)}
            onChange={handleMoveInDateChange}
          />
          {pendingMoveInError && (
            <p className="text-red" aria-label="pending-move-in-naur-error">
              {pendingMoveInError}
            </p>
          )}
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <label className="required">
            {intl.formatMessage(messages.leaseTermLabel)}
          </label>
        </Col>
        <Col xs={5}>
          <div className="form-group">
            <Field
              name="leaseTermId"
              component={renderSelectField}
              options={getLeaseTermOptions(leaseTerms)}
              onChange={onChangeLeaseTermId(formValues.startDate)}
              disabled={!isEditable}
            />
          </div>
        </Col>
        <Col xs={1}>
          <EditLeaseDataLink
            showEditLink={showEditLink}
            showLeaseDataModal={showLeaseDataModal}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <label className="required">
            {intl.formatMessage(messages.leaseStartDateLabel)}
          </label>
        </Col>
        <Col xs={5}>
          <Field
            bsSize="md"
            className="input-group"
            component={renderDateField}
            dateFormat={extractCurrentDateFormat(intl)}
            disabled={!isEditable}
            name="startDate"
            onChange={onChangeLeaseStartDate(formValues.leaseTermId)}
            placeholder={extractCurrentDateFormat(intl)}
            noticeToVacate={noticeToVacate}
          />
        </Col>
        <Col xs={1}>
          <EditLeaseDataLink
            showEditLink={showEditLink}
            showLeaseDataModal={showLeaseDataModal}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={6}>
          <label className="required">
            {intl.formatMessage(messages.leaseEndDateLabel)}
          </label>
        </Col>
        <Col xs={5}>
          <Field
            name="endDate"
            component={renderDateField}
            bsSize="md"
            placeholder={extractCurrentDateFormat(intl)}
            dateFormat={extractCurrentDateFormat(intl)}
            className="input-group"
            disabled={leaseEndDateDisabled}
            onChange={onChangeEndDate}
          />
        </Col>
        <Col xs={1}>
          <EditLeaseDataLink
            showEditLink={showEditLink}
            showLeaseDataModal={showLeaseDataModal}
          />
        </Col>
      </Row>
      {leaseExpirationMgmtV1 && isLeaseExpirationLimitsActive && (
        <Row>
          <Col xs={6}></Col>
          <Col xs={5}>
            {!isNil(lease.overrideLeaseExpirationLimitUpdatedAt) &&
              isEndDateOverLimit &&
              !endDateChanged && (
                <span
                  className="text-gray--darker"
                  aria-label="override-lease-expiration-legend"
                >
                  {intl.formatMessage(
                    messages.overrideLeaseExpirationLimitMessage,
                    {
                      ...lease.overrideLeaseExpirationLimitUpdatedBy,
                      date: moment(
                        lease.overrideLeaseExpirationLimitUpdatedAt,
                      ).format('MM/DD/YYYY'),
                    },
                  )}
                </span>
              )}
            <ElementWithPermissions
              scope={['lease-expirations-limit-override']}
            >
              <Field
                name="overrideLeaseExpirationLimit"
                component={renderCheckboxField}
                label={intl.formatMessage(
                  messages.overrideLeaseExpirationLimitLabel,
                )}
                disabled={!isEditable || !isEndDateOverLimit}
                aria-label="override-lease-expiration-limit"
              />
            </ElementWithPermissions>
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={6}>
          <label className="required">
            {intl.formatMessage(messages.numberOfSatellites)}
          </label>
        </Col>
        <Col xs={5}>
          <div className="form-group">
            <Field
              name="numSatellites"
              options={range(0, 4).map((n) => ({
                value: n,
                text: n,
                disabled: false,
              }))}
              component={renderSelectField}
              disabled={!isEditable}
            />
          </div>
        </Col>
      </Row>
    </Col>
  );
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators({ change, touch }, dispatch);
  return { actions };
}

export default connect(null, mapDispatchToProps)(LeaseBasicsRightColumn);
